import React, { useEffect, createElement, Component } from 'react';
import { Star, Clock, Repeat, HelpCircle, RotateCcw, CheckCircle2, Quote } from 'lucide-react';
import { CTAButton } from './components/CTAButton';
import { AccordionItem } from './components/Accordion';
import { ContentGroups } from './components/ContentGroups';
import { Countdown } from './components/Countdown';
import { PurchasePopup } from './components/PurchasePopup';
import { curriculum } from './data/curriculum';
import { bonuses } from './data/bonuses';
const totalTopics = curriculum.reduce((total, entry) => total + entry.groups.reduce((sum, group) => sum + group.items.length, 0), 0);
export function App() {
  // Meta Pixel — PageView
  useEffect(() => {
    const PIXEL_ID = '2782582688765590';
    const w = window as any;
    if (!w.fbq) {
      const n: any = w.fbq = function (...args: any[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      };
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const s = document.createElement('script');
      s.async = true;
      s.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const first = document.getElementsByTagName('script')[0];
      first?.parentNode?.insertBefore(s, first);
    }
    w.fbq('init', PIXEL_ID);
    w.fbq('track', 'PageView');
    // noscript fallback
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
    document.body.appendChild(img);
    return () => {
      if (img.parentNode) document.body.removeChild(img);
    };
  }, []);
  return <div className="min-h-screen bg-[#EAF7F1] font-sans text-gray-800 selection:bg-emerald-200" data-id="element-0" data-mp="3dmfolnkx4rmz">
      <PurchasePopup data-id="element-1" data-mp="nv0ypuwzss67" />

      {/* HERO SECTION */}
      <section className="pt-12 pb-16 px-4 max-w-4xl mx-auto text-center" data-id="element-2" data-mp="sudmqizlo5ad">
        <h1 className="text-3xl md:text-5xl font-extrabold text-teal-700 uppercase mb-8 leading-tight tracking-tight" data-id="element-3" data-mp="swgk5o3310p4">
          Dejá de improvisar tus clases de gimnasia postural
        </h1>

        <div className="relative w-full max-w-2xl mx-auto mb-8" data-id="element-4" data-mp="3uevqzx020421">
          <a href="https://checkout.ebookbtnlines.shop/cart/45530178650159:1" className="block" data-id="element-5" data-mp="d3cvprys4wgk">
            <img src="https://cdn.magicpatterns.com/uploads/9UqNhFhe5bVffpVJzg5Rxu/GP_SIN_FONDO.png" alt="Gimnasia Postural Resuelta Mockup" className="w-full h-auto drop-shadow-2xl hover:scale-[1.02] transition-transform duration-300" data-id="element-6" data-mp="83i6dsunkexm" />
          </a>
        </div>

        <h2 className="text-lg md:text-xl text-gray-700 font-medium mb-6 max-w-2xl mx-auto leading-relaxed" data-id="element-7" data-mp="3lkhbbtbm2rie">
          Todo lo que necesitás para crear clases de gimnasia postural más
          organizadas, profesionales y seguras.
        </h2>

        <div className="flex items-center justify-center gap-1 mb-8" data-id="element-8" data-mp="2sv0qphu8gewq">
          {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-9" data-mp="18cgflw44u7mp" data-mpi={i} />)}
          <span className="ml-2 font-bold text-gray-800" data-id="element-10" data-mp="1d62iwxoeoz5u">4.9/5 estrellas</span>
        </div>

        <CTAButton data-id="element-11" data-mp="ygwix7hqv4ww" />

        <p className="mt-4 text-teal-700 font-bold uppercase tracking-widest text-sm" data-id="element-12" data-mp="ydii43oqb3rk">
          Oferta especial solo por hoy
        </p>
      </section>

      {/* MODULE 1: PAIN POINTS */}
      <section className="py-16 px-4 bg-white" data-id="element-13" data-mp="1r526s343ha0y">
        <div className="max-w-4xl mx-auto" data-id="element-14" data-mp="1w3vltzz15938">
          <div className="text-center mb-12" data-id="element-15" data-mp="1w2tzv0gh726q">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4" style={{
            fontSize: "40px"
          }} data-id="element-16" data-mp="1i3ujsh713vm6">
              ¿Te pasa esto?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-17" data-mp="1m3ctshxbama8">
              Sabemos lo frustrante que es llegar a una clase improvisando y utilizando las mismas herramientas una y otra vez.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-18" data-mp="ano9xzfcu3dn">
            <PainItem icon={<Clock className="w-7 h-7 text-teal-600" data-id="element-20" data-mp="qyyrnr059wot" />} text="Pasás horas buscando ejercicios para preparar una sola clase." data-id="element-19" data-mp="qyqa3lt2u37a" />
            <PainItem icon={<Repeat className="w-7 h-7 text-teal-600" data-id="element-22" data-mp="h59i8nci2y69" />} text="Terminás usando siempre las mismas rutinas porque se te acaban las ideas." data-id="element-21" data-mp="h8414afv8axk" />
            <PainItem icon={<HelpCircle className="w-7 h-7 text-teal-600" data-id="element-24" data-mp="19tx86w872bq5" />} text="Dudás cómo adaptar los ejercicios cuando aparece un alumno con una alteración postural específica." data-id="element-23" data-mp="19uoshawccn14" />
            <PainItem icon={<RotateCcw className="w-7 h-7 text-teal-600" data-id="element-26" data-mp="vw8mjm6az0k1" />} text="Sentís que cada semana volvés a empezar desde cero para planificar tus clases." data-id="element-25" data-mp="zw0p9i90mchh" />
          </div>
        </div>
      </section>

      {/* MODULE 2: BENEFITS */}
      <section className="py-16 px-4 bg-[#E3F0F7]" style={{
      paddingTop: "6px",
      paddingBottom: "6px"
    }} data-id="element-27" data-mp="3gkwcaqt5nzc0">
        <div className="max-w-4xl mx-auto" data-id="element-28" data-mp="1w5zy8e7pkwlg">
          <h2 className="text-2xl md:text-4xl font-bold text-center text-gray-900 mb-12" style={{
          fontSize: "28px",
          paddingTop: "020px",
          paddingBottom: "020px"
        }} data-id="element-29" data-mp="205quuyjx26xw">
            Lo que vas a lograr con Gimnasia Postural Resuelta...
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-30" data-mp="29wlfxzlff0hr">
            <BenefitCard title="Ahorrá tiempo planificando" desc="Accedé a clases listas, programas completos y cientos de ejercicios organizados para dejar de empezar desde cero cada semana." data-id="element-31" data-mp="2evg0b5z1kx3z" />
            <BenefitCard title="Enseñá con más seguridad" desc="Aprendé a evaluar, adaptar y progresar cada ejercicio según las necesidades de tus alumnos, utilizando un método claro y fácil de aplicar." data-id="element-32" data-mp="2nv339w7f6az5" />
            <BenefitCard title="Clases más variadas y profesionales" desc="Incorporá nuevos recursos con ejercicios utilizando banda elástica, pelota y palo, además de material específico para adultos mayores y embarazadas." data-id="element-33" data-mp="tid7ta1ektpn" />
            <BenefitCard title="Dejá de improvisar" desc="Trabajá con un sistema completo que te permita preparar tus clases más rápido, reducir el estrés y volver a disfrutar de enseñar." data-id="element-34" data-mp="21sdhiasoy3ke" />
          </div>

          <div className="mt-12 text-center" data-id="element-35" data-mp="2attp6as4frkx">
            <CTAButton data-id="element-36" data-mp="25tfeekz4y3j7" />
          </div>
        </div>
      </section>

      {/* MODULE 3: CURRICULUM */}
      <section className="py-20 px-4 bg-white relative overflow-hidden" data-id="element-37" data-mp="2kq6fcyogjnax">
        <div className="absolute -top-24 -left-24 w-72 h-72 bg-emerald-50 rounded-full" aria-hidden="true" data-id="element-38" data-mp="2ko3jhy0mizya" />
        <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-[#E3F0F7] rounded-full" aria-hidden="true" data-id="element-39" data-mp="2tizzb9eunav4" />

        <div className="max-w-3xl mx-auto relative" data-id="element-40" data-mp="2phy9fj6tob5t">
          <div className="bg-teal-700 rounded-3xl px-4 py-5 md:px-6 text-center shadow-xl mb-8" data-id="element-41" data-mp="33ged119e1ljq">
            <span className="inline-block bg-white/15 text-white text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full mb-2" data-id="element-42" data-mp="2yicv4jnp7s9u">
              Programa completo
            </span>
            <h2 className="text-lg md:text-2xl font-extrabold text-white mb-1 uppercase tracking-tight" data-id="element-43" data-mp="3n398uwkuu9ej">
              Gimnasia Postural Resuelta
            </h2>
            <p className="text-emerald-100 text-xs md:text-sm" data-id="element-44" data-mp="387jn8zjkzyac">
              Formación completa para profesionales de la gimnasia postural
            </p>

            <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/20" data-id="element-45" data-mp="2zc3t0hewzh48">
              <div data-id="element-46" data-mp="3443n3t8azfsw">
                <p className="text-lg md:text-xl font-black text-white" data-id="element-47" data-mp="344n5fb1v867g">
                  {curriculum.length}
                </p>
                <p className="text-[9px] md:text-[10px] uppercase tracking-wider text-emerald-100" data-id="element-48" data-mp="2pd4jyxq8ljgp">
                  Tomos
                </p>
              </div>
              <div className="border-x border-white/20" data-id="element-49" data-mp="2gfkflfx2v71v">
                <p className="text-lg md:text-xl font-black text-white" data-id="element-50" data-mp="2kfmly2drconh">
                  {totalTopics}+
                </p>
                <p className="text-[9px] md:text-[10px] uppercase tracking-wider text-emerald-100" data-id="element-51" data-mp="30uc882uli8lt">
                  Temas
                </p>
              </div>
              <div data-id="element-52" data-mp="2qx7hrqtpvqz7">
                <p className="text-lg md:text-xl font-black text-white" data-id="element-53" data-mp="2qwoad41p0kzf">∞</p>
                <p className="text-[9px] md:text-[10px] uppercase tracking-wider text-emerald-100" data-id="element-54" data-mp="2ly4nn1f9z560">
                  Acceso de por vida
                </p>
              </div>
            </div>
          </div>

          {curriculum.map((entry, index) => <AccordionItem key={entry.title} title={entry.title} badge={String(index + 1).padStart(2, '0')} isOpen={index === 0} data-id="element-55" data-mp="2d485ryt58ewe" data-mpi={index}>
              <ContentGroups groups={entry.groups} data-id="element-56" data-mp="2396fv0r1n8sr" />
            </AccordionItem>)}

          <div className="mt-10 text-center" data-id="element-57" data-mp="1y78rxt7p7pth">
            <CTAButton text="QUIERO PLANIFICAR MÁS RÁPIDO" data-id="element-58" data-mp="1u6x5cavrzffd" />
          </div>
        </div>
      </section>

      {/* MODULE 4: TESTIMONIALS */}
      <section className="py-16 px-4 bg-[#EAF7F1]" data-id="element-59" data-mp="9nuuhijt0bso">
        <div className="max-w-5xl mx-auto" data-id="element-60" data-mp="dqhonq3y68t1">
          <div className="text-center mb-12" data-id="element-61" data-mp="dpg8x8bzky4z">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-62" data-mp="dscb58uva8z8">
              Profesionales que ya llevaron sus clases al siguiente nivel
            </h2>
            <p className="text-lg text-gray-600" data-id="element-63" data-mp="no5lcll45qdu">
              Más de 500 profesionales de Argentina ya utilizan nuestro sistema.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-64" data-mp="3r4mf0fokpusb">
            <TestimonialCard name="Camila, 31 años" city="Córdoba" quote="Por primera vez siento que tengo un criterio claro para adaptar ejercicios según cada alumno." data-id="element-65" data-mp="3w4qvi5phepij" />
            <TestimonialCard name="Florencia, 38 años" city="Rosario" quote="Antes improvisaba muchísimo. Hoy entro a cada clase sabiendo exactamente qué hacer." data-id="element-66" data-mp="33es2y0c2wa94" />
            <TestimonialCard name="Luciana, 35 años" city="Buenos Aires" quote="Mis alumnos empezaron a notar mejoras mucho más rápido y eso hizo crecer muchísimo las recomendaciones." data-id="element-67" data-mp="2f6c0jskuh3su" />
            <TestimonialCard name="Valeria, 42 años" city="Mendoza" quote="El módulo de desalineaciones me dio herramientas que nunca había aprendido en otras formaciones." data-id="element-68" data-mp="2u4e2xzg53j8i" />
            <TestimonialCard name="Mariana, 29 años" city="La Plata" quote="Me ayudó muchísimo a sentirme más segura trabajando con alumnos con molestias y patologías frecuentes." data-id="element-69" data-mp="21exetdzjyoi3" />
          </div>
        </div>
      </section>

      {/* MODULE 5: BONUSES */}
      <section className="py-16 px-4 bg-white" data-id="element-70" data-mp="1dp34b32phc10">
        <div className="max-w-3xl mx-auto" data-id="element-71" data-mp="1dobgwfj9iklt">
          <div className="text-center mb-10" data-id="element-72" data-mp="1io7arly8wvnn">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-73" data-mp="1in4o5q7yhfdj">
              Oferta por tiempo limitado
            </h2>
            <p className="text-xl text-teal-700 font-bold uppercase tracking-wide" data-id="element-74" data-mp="3rxdsncsbhl6i">
              Llevate estos BONOS GRATIS hoy
            </p>
          </div>

          {bonuses.map((entry, index) => <AccordionItem key={entry.title} title={entry.title} previewImage={entry.previewImage} isOpen={index === 0} data-id="element-75" data-mp="3i0itcro7zgr6" data-mpi={index}>
              <ContentGroups groups={entry.groups} data-id="element-76" data-mp="389ed7omt5pur" />
            </AccordionItem>)}
        </div>
      </section>

      {/* MODULE 6: OFFER */}
      <section className="py-20 px-4 bg-teal-700" data-id="element-77" data-mp="348kq2xh6wf2x">
        <div className="max-w-xl mx-auto text-center bg-white p-8 md:p-12 rounded-[2rem] shadow-2xl ring-4 ring-teal-500/30 border border-emerald-100 relative overflow-hidden" data-id="element-78" data-mp="2za12ytqv0x8z">
          {/* corner accent */}
          <div className="absolute -top-16 -right-16 w-40 h-40 bg-emerald-100 rounded-full opacity-60" data-id="element-79" data-mp="2pc3gaaonf8ih" />
          <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-teal-100 rounded-full opacity-50" data-id="element-80" data-mp="2fmemujr8xy37" />

          <div className="relative" data-id="element-81" data-mp="2knc6l0rwn06t">
            <span className="inline-block bg-teal-700 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6 animate-pulse" data-id="element-82" data-mp="2kk6gbi7jhs9i">
              🔥 Oferta por tiempo limitado
            </span>

            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3" data-id="element-83" data-mp="2ye7i6ktsfve3">
              Esto es todo lo que te llevás
            </h2>
            <p className="text-gray-600 max-w-md mx-auto mb-2 leading-relaxed" data-id="element-84" data-mp="33bpjpq5h78c2">
              Acceso inmediato y de por vida a{' '}
              <span className="font-semibold text-teal-700" data-id="element-85" data-mp="2pjt58owqcwy5">
                Gimnasia Postural Resuelta
              </span>{' '}
              más todos los bonos de regalo.
            </p>

            <Countdown data-id="element-86" data-mp="3hap58oxoz740" />

            <div className="my-8 bg-emerald-50 rounded-2xl py-6 px-4 border border-emerald-100" data-id="element-87" data-mp="3m3idhmsdcxku">
              <p className="text-gray-500 text-lg line-through decoration-gray-400 decoration-2 mb-1" data-id="element-88" data-mp="1aojdce4b2epb">
                Precio regular: $49.990
              </p>
              <p className="text-5xl md:text-6xl font-black text-teal-700 leading-none" data-id="element-89" data-mp="15p6ib63a61bn">
                $19.990{' '}
                <span className="text-xl text-gray-500 font-medium" data-id="element-90" data-mp="1kg71o65kffmk">ARS</span>
              </p>
              <span className="inline-block mt-3 bg-teal-700 text-white text-sm font-bold px-4 py-1.5 rounded-full" data-id="element-91" data-mp="1fhciv47llr2g">
                Ahorrás $30.000 hoy
              </span>
            </div>

            <CTAButton className="text-lg py-5 shadow-teal-700/30" data-id="element-92" data-mp="msf5nfi8ukgk" />

            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500" data-id="element-93" data-mp="11omly4qj71qk">
              <CheckCircle2 className="w-4 h-4 text-teal-600" data-id="element-94" data-mp="wnqmc3d7q1qy" />
              <span data-id="element-95" data-mp="2d2rrv3ab1loj">Pago seguro y acceso inmediato</span>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 text-center text-gray-500 text-sm bg-white" data-id="element-96" data-mp="2835durmnk1eo">
        <p data-id="element-97" data-mp="hnlb4qrgn62z">
          © {new Date().getFullYear()} Gimnasia Postural Resuelta. Todos los
          derechos reservados.
        </p>
      </footer>
    </div>;
}
// Helper Components
function PainItem({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <div className="bg-emerald-50/60 p-6 rounded-3xl border border-emerald-100 flex items-start gap-4 hover:shadow-lg transition-shadow" data-id="element-98" data-mp="vmkzdxb29iym">
      <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-sm flex-shrink-0" data-id="element-99" data-mp="10kmigi8hlia3">
        {icon}
      </div>
      <p className="text-gray-700 leading-relaxed pt-1" data-id="element-100" data-mp="15inzyyq9l9jg">{text}</p>
    </div>;
}
function BenefitCard({
  title,
  desc
}: {
  title: string;
  desc: string;
}) {
  return <div className="bg-white p-8 rounded-3xl shadow-sm border border-emerald-50 flex gap-4 hover:shadow-md transition-shadow" data-id="element-101" data-mp="1ec11uq6b3lho">
      <div className="flex-shrink-0 mt-1" data-id="element-102" data-mp="1je05m15ykkf5">
        <CheckCircle2 className="w-6 h-6 text-teal-600" data-id="element-103" data-mp="1oaqkwm9xvbzi" />
      </div>
      <div data-id="element-104" data-mp="1o8xd33nlalpx">
        <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-105" data-mp="1yf715grbyv3u">{title}</h3>
        <p className="text-gray-600 leading-relaxed" data-id="element-106" data-mp="1ygi0n4dqtgp5">{desc}</p>
      </div>
    </div>;
}
function TestimonialCard({
  name,
  city,
  quote
}: {
  name: string;
  city: string;
  quote: string;
}) {
  return <div className="bg-white p-8 rounded-3xl shadow-sm border border-emerald-50 relative" data-id="element-107" data-mp="1oj3q1lei3m4r">
      <Quote className="absolute top-6 right-6 w-8 h-8 text-emerald-100" data-id="element-108" data-mp="1khif45lj1hx7" />
      <div className="flex items-center gap-4 mb-6" data-id="element-109" data-mp="2h4dcujtpimg5">
        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center text-teal-700 font-bold text-xl" data-id="element-110" data-mp="2c6vbpflxlbip">
          {name.charAt(0)}
        </div>
        <div data-id="element-111" data-mp="27dsf0yluxlya">
          <h4 className="font-bold text-gray-900" data-id="element-112" data-mp="27diqlfggnmup">{name}</h4>
          <p className="text-sm text-gray-500" data-id="element-113" data-mp="23ds1rhahvgst">{city}</p>
        </div>
      </div>
      <p className="text-gray-700 italic leading-relaxed relative z-10" data-id="element-114" data-mp="2zx7qnaflg8is">
        "{quote}"
      </p>
    </div>;
}